import { useEffect, useRef, useState } from "react";
import { RotateCw } from "lucide-react";
import { useUploadQueue } from "../context/UploadQueueContext";
import { toast } from "../utils/toast";

const THRESHOLD = 70; // px of (damped) pull that triggers a refresh
const MAX_PULL = 110;

// Installed (Home Screen) app only: browsers have their own pull-to-refresh,
// and a second one there would fire twice.
const isInstalledApp = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

/**
 * Pull down at the top of the page to reload. Attaches to the page scroller
 * (Layout's <main>). Reloads the whole app, so it also picks up a new version.
 */
export default function PullToRefresh({ scrollerRef }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [top, setTop] = useState(0); // where the page starts, below the header/banners
  const pullRef = useRef(0);
  const { tasks } = useUploadQueue();
  const uploadingRef = useRef(false);
  uploadingRef.current = tasks.some((t) => t.status === "pending" || t.status === "uploading");

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !isInstalledApp()) return undefined;
    let startY = null;

    // The PAGE slides down by the pull, opening a gap above it for the circle.
    // The browser's own bounce is turned off on the scroller so it can't fight it.
    el.style.overscrollBehaviorY = "none";
    const show = (v, animate = false) => {
      pullRef.current = v;
      setPull(v);
      el.style.transition = animate ? "transform 200ms ease-out" : "";
      // No transform at rest: one would make it the containing block for any
      // `fixed` popover rendered inside the page.
      el.style.transform = v ? `translateY(${v}px)` : "";
    };
    // Every scroller between the finger and the page must be at its top, and
    // the map pans with a downward drag — neither may start a refresh.
    const canStart = (target) => {
      if (target.closest?.(".leaflet-container")) return false;
      for (let n = target; n && n !== el.parentElement; n = n.parentElement) {
        if (n.scrollTop > 0) return false;
      }
      return true;
    };
    const onStart = (e) => {
      startY = e.touches.length === 1 && canStart(e.target) ? e.touches[0].clientY : null;
      if (startY != null) setTop(el.getBoundingClientRect().top);
    };
    const onMove = (e) => {
      if (startY == null) return;
      const dy = e.touches[0].clientY - startY;
      show(dy > 0 ? Math.min(MAX_PULL, dy * 0.5) : 0);
    };
    const onEnd = () => {
      if (startY == null) return;
      startY = null;
      const pulled = pullRef.current;
      if (pulled < THRESHOLD) return show(0, true);
      if (uploadingRef.current) {
        show(0, true);
        toast.warning("Photos are still uploading — refresh once they finish.");
        return;
      }
      show(THRESHOLD, true); // hold the gap open, spinning, until the reload
      setRefreshing(true);
      window.location.reload();
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: true });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
      el.style.transform = "";
      el.style.overscrollBehaviorY = "";
    };
  }, [scrollerRef]);

  if (!pull && !refreshing) return null;
  const offset = refreshing ? THRESHOLD : pull;
  // Centred in the gap the page opened (the circle is 36px tall).
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-dropdown pointer-events-none"
      style={{ top: top + Math.max(0, offset / 2 - 18), opacity: Math.min(1, offset / 40) }}
      aria-hidden="true"
    >
      <div className="flex items-center justify-center w-9 h-9 rounded-full bg-surface shadow-md border border-line">
        <RotateCw
          className={`w-4 h-4 ${offset >= THRESHOLD ? "text-brand-600" : "text-ink-muted"} ${refreshing ? "animate-spin" : ""}`}
          style={refreshing ? undefined : { transform: `rotate(${offset * 4}deg)` }}
        />
      </div>
    </div>
  );
}
